import { useEffect } from 'react';
import { getScriptUrl } from '../config';

export const getPagarme3DSToken = async (): Promise<string> => {
    const baseUrl = import.meta.env.VITE_BASE_API_URL;

    const response = await fetch(`${baseUrl}api/financial/3ds/token/?provider=pagarme`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        }
    });

    if (!response.ok) {
        throw new Error(`Error fetching Pagar.me token: ${response.status}`);
    }

    const raw = await response.text();

    const extractToken = (payload: unknown): string | null => {
        if (!payload) return null;
        if (typeof payload === 'object' && !Array.isArray(payload)) {
            const obj = payload as Record<string, any>;
            return obj.tds_token || obj.token || null;
        }
        if (Array.isArray(payload)) {
            const joined = (payload as unknown[]).map(String).join('');
            try {
                const parsed = JSON.parse(joined);
                if (parsed && typeof parsed === 'object') {
                    return parsed.tds_token || parsed.token || null;
                }
            } catch {
                const m = joined.match(/"tds_token"\s*:\s*"([^"]+)"/);
                if (m) return m[1];
            }
            return null;
        }
        if (typeof payload === 'string') {
            try {
                const parsed = JSON.parse(payload);
                return extractToken(parsed);
            } catch {
                const m = payload.match(/"tds_token"\s*:\s*"([^"]+)"/);
                if (m) return m[1];
            }
        }
        return null;
    };

    let token: string | null = null;
    try {
        const asJson = JSON.parse(raw);
        token = extractToken(asJson);
    } catch {
        token = extractToken(raw);
    }

    if (!token || typeof token !== 'string' || token.trim() === '') {
        throw new Error('Invalid Pagar.me 3DS token from backend');
    }

    return token;
};

declare global {
    interface Window {
        Script3ds?: {
            init3ds: (token: string, tdsData: any, callback: (data: any) => void, challengeWindowSize: string) => void;
        };
    }
}

export type PagarmeThreeDSResult = {
    trans_status?: string;
    tds_server_trans_id?: string;
    error?: string;
};

export type AddressData = {
    street: string;
    line_1: string;
    line_2?: string;
    city: string;
    state: string;
    country: string;
    post_code: string;
};

export type PhoneData = {
    country_code: string;
    subscriber: string;
    phone_type: 'home' | 'work' | 'mobile';
};

export type PurchaseData = {
    amount: number;
    currency: string;
    date: string;
    instal_data?: number;
};

export type MerchantData = {
    name: string;
};

export type PagarmeThreeDSData = {
    bill_addr: AddressData;
    ship_addr: AddressData;
    email: string;
    phones: PhoneData[];
    merchant: MerchantData;
    purchase: PurchaseData;
    acct_type?: '01' | '02' | '03';
};

type PagarmeThreeDSecureProps = {
    token: string;
    tdsData: PagarmeThreeDSData;
    cardData: {
        holderName: string;
        cardNumber: string;
        cvv: string;
        expMonth: string;
        expYear: string;
    };
    onAuthResult?: (result: PagarmeThreeDSResult) => void;
    challengeWindowSize?: '01' | '02' | '03' | '04' | '05';
};

export function PagarmeThreeDSecure({
    token,
    tdsData,
    cardData,
    onAuthResult,
    challengeWindowSize = '05'
}: PagarmeThreeDSecureProps) {

    useEffect(() => {
        const initializePagarme3DS = () => {
            try {
                if (!token || typeof token !== 'string') {
                    throw new Error('Token 3DS inválido ou ausente');
                }

                let form = document.querySelector('form[data-pagarmecheckout-form]') as HTMLFormElement;
                if (!form) {
                    form = document.createElement('form');
                    form.setAttribute('data-pagarmecheckout-form', '');
                    form.style.display = 'none';
                    document.body.appendChild(form);
                }

                const ensureField = (elementName: string, value: string, type: 'input' | 'span' = 'input') => {
                    let field = form.querySelector(`[data-pagarmecheckout-element="${elementName}"]`);
                    if (!field) {
                        if (type === 'span') {
                            field = document.createElement('span');
                        } else {
                            field = document.createElement('input');
                            (field as HTMLInputElement).type = 'hidden';
                        }
                        field.setAttribute('data-pagarmecheckout-element', elementName);
                        form.appendChild(field);
                    }

                    if (field instanceof HTMLInputElement) {
                        field.value = value;
                    } else if (field instanceof HTMLSpanElement) {
                        field.textContent = value;
                    }
                };

                ensureField('holder_name', cardData.holderName);
                ensureField('number', cardData.cardNumber.replace(/\D/g, ''));
                ensureField('cvv', cardData.cvv);
                ensureField('exp_date', `20${cardData.expYear}-${cardData.expMonth.padStart(2, '0')}`);
                ensureField('brand', '', 'span');

                let iframe = form.querySelector('#challengeIframe') as HTMLIFrameElement;
                if (!iframe) {
                    iframe = document.createElement('iframe');
                    iframe.id = 'challengeIframe';
                    iframe.name = 'challengeIframe';
                    iframe.setAttribute('data-pagarmecheckout-element', 'challengeIframe');
                    iframe.style.display = 'none';
                    iframe.style.width = '100%';
                    iframe.style.height = '400px';
                    form.appendChild(iframe);
                }

                const callback = (data: PagarmeThreeDSResult) => {
                    if (data?.error) {
                        console.error('Pagar.me 3DS Error:', data.error);
                    }
                    onAuthResult?.(data);
                    return true;
                };

                const validateTdsData = (data: PagarmeThreeDSData): boolean => {
                    const requiredFields = ['bill_addr', 'ship_addr', 'email', 'phones', 'merchant', 'purchase'];
                    for (const field of requiredFields) {
                        if (!data[field as keyof PagarmeThreeDSData]) {
                            return false;
                        }
                    }
                    if (!data.bill_addr.street || !data.bill_addr.city || !data.bill_addr.post_code) return false;
                    if (!data.phones.length || !data.phones[0].subscriber) return false;
                    if (!data.purchase.amount || !data.purchase.currency || !data.purchase.date) return false;
                    return true;
                };

                if (!validateTdsData(tdsData)) {
                    onAuthResult?.({ error: 'Dados obrigatórios ausentes para 3DS' });
                    return;
                }

                const cleanTdsData = JSON.parse(JSON.stringify(tdsData));

                if (window.Script3ds?.init3ds) {
                    window.Script3ds.init3ds(token, cleanTdsData, callback, '03');
                } else {
                    onAuthResult?.({ error: 'SDK Pagar.me não carregado' });
                }

            } catch (error) {
                onAuthResult?.({ error: error instanceof Error ? error.message : 'Erro desconhecido' });
            }
        };

        const loadScript = () => {
            const existingScript = document.getElementById('pagarme-3ds-script');
            if (existingScript) {
                existingScript.remove();
            }

            const script = document.createElement('script');
            script.id = 'pagarme-3ds-script';
            const pagarme3dsScriptUrl = import.meta.env.VITE_PAGARME_3DS_SCRIPT || getScriptUrl('pagarme');
            script.src = pagarme3dsScriptUrl;

            script.onload = () => {
                setTimeout(initializePagarme3DS, 200);
            };

            script.onerror = () => {
                onAuthResult?.({ error: 'Falha ao carregar SDK Pagar.me' });
            };

            document.head.appendChild(script);
        };

        if (window.Script3ds?.init3ds) {
            initializePagarme3DS();
        } else {
            loadScript();
        }

        return () => {
            const script = document.getElementById('pagarme-3ds-script');
            if (script) {
                script.remove();
            }
        };
    }, [token, tdsData, cardData, onAuthResult, challengeWindowSize]);

    return (
        <div style={{ display: 'none' }} />
    );
}

export const createPagarmeTdsData = (checkoutData: {
    amount: number;
    currency?: string;
    email: string;
    name: string;
    phone: string;
    address: {
        street: string;
        number: string;
        complement?: string;
        city: string;
        state: string;
        zipcode: string;
        freight?: string;
    };
    paymentMethod: 'credit' | 'debit';
}): PagarmeThreeDSData => {
    const phoneClean = checkoutData.phone.replace(/\D/g, '');
    const countryCode = phoneClean.startsWith('55') ? '55' : '55';
    const subscriber = phoneClean.startsWith('55') ? phoneClean.substring(2) : phoneClean;

    const addressData: AddressData = {
        street: checkoutData.address.street,
        line_1: checkoutData.address.number,
        line_2: checkoutData.address.complement || '',
        city: checkoutData.address.city,
        state: checkoutData.address.state,
        country: 'BRA',
        post_code: checkoutData.address.zipcode.replace(/\D/g, '')
    };

    const now = new Date();
    const formatRfc3339Utc = () => {
        const yyyy = now.getUTCFullYear().toString();
        const MM = String(now.getUTCMonth() + 1).padStart(2, '0');
        const dd = String(now.getUTCDate()).padStart(2, '0');
        const hh = String(now.getUTCHours()).padStart(2, '0');
        const mm = String(now.getUTCMinutes()).padStart(2, '0');
        const ss = String(now.getUTCSeconds()).padStart(2, '0');
        return `${yyyy}${MM}${dd}${hh}${mm}${ss}`;
    };

    return {
        bill_addr: addressData,
        ship_addr: addressData,
        email: checkoutData.email,
        phones: [
            {
                country_code: countryCode,
                subscriber: subscriber,
                phone_type: 'mobile'
            }
        ],
        merchant: {
            name: 'Cakto'
        },
        purchase: {
            amount: checkoutData.amount,
            currency: checkoutData.currency || 'BRL',
            date: formatRfc3339Utc()
        },
        acct_type: checkoutData.paymentMethod === 'credit' ? '02' : '03'
    };
};
